import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RecentActivity = ({ activities }) => {
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All Activity', icon: 'Activity' },
    { id: 'donations', label: 'Donations', icon: 'Heart' },
    { id: 'updates', label: 'Updates', icon: 'Bell' },
    { id: 'milestones', label: 'Milestones', icon: 'Trophy' }
  ];

  const filteredActivities = activities?.filter(activity => 
    filter === 'all' || activity?.type === filter
  );

  const displayedActivities = showAll 
    ? filteredActivities 
    : filteredActivities?.slice(0, 5);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'donations':
        return 'Heart';
      case 'updates':
        return 'Bell';
      case 'milestones':
        return 'Trophy';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'donations':
        return 'text-primary';
      case 'updates':
        return 'text-secondary';
      case 'milestones':
        return 'text-accent';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInHours = Math.floor((now - activityTime) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-card-foreground">Recent Activity</h2>
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredActivities?.length} activities
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1 overflow-x-auto">
        {filters?.map((filterOption) => (
          <button
            key={filterOption?.id}
            onClick={() => setFilter(filterOption?.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro whitespace-nowrap ${
              filter === filterOption?.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={filterOption?.icon} size={16} />
            <span>{filterOption?.label}</span>
          </button>
        ))}
      </div>
      {/* Activity List */}
      <div className="space-y-4">
        {displayedActivities?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No activities found for this filter.</p>
          </div>
        ) : (
          displayedActivities?.map((activity) => (
            <div
              key={activity?.id}
              className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted/50 transition-micro"
            >
              {/* Activity Icon */}
              <div className={`w-10 h-10 rounded-full bg-muted flex items-center justify-center ${getActivityColor(activity?.type)}`}>
                <Icon name={getActivityIcon(activity?.type)} size={20} />
              </div>

              {/* Activity Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-card-foreground">
                      {activity?.user && (
                        <span className="font-medium">{activity?.user}</span>
                      )}
                      {activity?.user && ' '}
                      {activity?.description}
                    </p>
                    {activity?.amount && (
                      <p className="text-lg font-semibold text-primary mt-1">
                        ${activity?.amount?.toLocaleString()}
                      </p>
                    )}
                    {activity?.details && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {activity?.details}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground ml-4 whitespace-nowrap">
                    {formatTimeAgo(activity?.timestamp)}
                  </div>
                </div>

                {/* Activity Image */}
                {activity?.image && (
                  <div className="mt-3">
                    <Image
                      src={activity?.image}
                      alt="Activity"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                )}

                {/* Activity Actions */}
                {activity?.actions && (
                  <div className="flex items-center space-x-4 mt-3">
                    {activity?.actions?.map((action, index) => (
                      <button
                        key={index}
                        className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-micro"
                      >
                        <Icon name={action?.icon} size={14} />
                        <span>{action?.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {/* Show More/Less Button */}
      {filteredActivities?.length > 5 && (
        <div className="text-center mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAll(!showAll)}
            iconName={showAll ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {showAll ? 'Show Less' : `Show ${filteredActivities?.length - 5} More`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;